import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CREW_LIST_KEY, CREW_PEOPLE_KEY, useRosterVesselId } from '@/pages/Crew/crewRoster';
import { useVessels } from '@/store/vessels/vessels.store';
import { DEFAULT_TIME_ZONE, dayInTimeZone, todayInTimeZone } from '@/utils/timezone';

import {
  createLeave,
  declineLeave,
  deleteLeave,
  getAwayLeaves,
  getLeaveConflicts,
  getLeaves,
  leaveTransition,
  updateLeave,
} from './leaves.api';
import type { CreateLeaveDto, LeavesFilterDto, UpdateLeaveDto } from './leaves.dto';
import { formatAwayLabel } from './leaves.mappers';

export const LEAVES_KEY = 'leaves';

/** The active vessel's leaves; the filter is part of the key, so each cut caches on its own. */
export const useLeavesQuery = (filter: LeavesFilterDto = {}, enabled = true) => {
  const vesselId = useRosterVesselId();

  return useQuery({
    queryKey: [LEAVES_KEY, vesselId, filter],
    queryFn: () => getLeaves(filter),
    enabled: enabled && !!vesselId,
  });
};

/**
 * Who is away over an event's days. Waits for both days; someone with no leaves of their own (a
 * guest) is refused, and then simply nobody shows as away.
 */
export const useAwayLeavesQuery = (params: { from?: string; to?: string }) => {
  const vesselId = useRosterVesselId();
  const { from, to } = params;

  return useQuery({
    queryKey: [LEAVES_KEY, 'away', vesselId, from, to],
    queryFn: () => getAwayLeaves({ from: from!, to: to! }),
    enabled: !!vesselId && !!from && !!to,
    retry: false,
  });
};

/**
 * Who is on leave over an event's days, as the vessel's calendar reads them, each with what to say
 * about it — `On leave: Apr 9 – Apr 11, 2027`. For the pickers that seat people on events and
 * trips: those away go last, the label beside their name. Without a start, the day is today.
 */
export const useAwayLabels = (
  start?: Date | string | null,
  end?: Date | string | null,
): ReadonlyMap<string, string> => {
  const timeZone = useVessels((state) => state.vessel?.timezone) || DEFAULT_TIME_ZONE;
  const from = start ? dayInTimeZone(new Date(start), timeZone) : todayInTimeZone(timeZone);
  const to = end ? dayInTimeZone(new Date(end), timeZone) : from;
  const { data: leaves } = useAwayLeavesQuery({ from, to: to < from ? from : to });

  return useMemo(() => {
    const labels = new Map<string, string>();
    // Earliest first, so someone with two leaves in the range is named by the one that comes first.
    [...(leaves ?? [])]
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .forEach((leave) => {
        if (!labels.has(leave.personId)) labels.set(leave.personId, formatAwayLabel(leave));
      });
    return labels;
  }, [leaves]);
};

/** The events a leave over these days would overlap. Waits for a start day. */
export const useLeaveConflictsQuery = (params: {
  personId?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const vesselId = useRosterVesselId();
  const { personId, startDate, endDate } = params;

  return useQuery({
    queryKey: [LEAVES_KEY, 'conflicts', vesselId, personId, startDate, endDate],
    queryFn: () =>
      getLeaveConflicts({ person_id: personId, start_date: startDate!, end_date: endDate }),
    enabled: !!vesselId && !!startDate,
  });
};

/**
 * Every leave write moves the same three reads: the leaves themselves, and the roster's crew
 * and people reads, which carry the On Leave presence a leave turns on and off.
 */
const useInvalidateLeaves = () => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [LEAVES_KEY] }),
      queryClient.invalidateQueries({ queryKey: [CREW_LIST_KEY] }),
      queryClient.invalidateQueries({ queryKey: [CREW_PEOPLE_KEY] }),
    ]);
};

export const useCreateLeaveMutation = () => {
  const invalidate = useInvalidateLeaves();

  return useMutation({
    mutationFn: (body: CreateLeaveDto) => createLeave(body),
    onSuccess: invalidate,
  });
};

export const useUpdateLeaveMutation = () => {
  const invalidate = useInvalidateLeaves();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLeaveDto }) => updateLeave(id, body),
    onSuccess: invalidate,
  });
};

export const useDeclineLeaveMutation = () => {
  const invalidate = useInvalidateLeaves();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => declineLeave(id, { reason }),
    onSuccess: invalidate,
  });
};

/** Approve, cancel and "back from leave": one write each, nothing to send but the leave. */
export const useDeleteLeaveMutation = () => {
  const invalidate = useInvalidateLeaves();

  return useMutation({ mutationFn: deleteLeave, onSuccess: invalidate });
};

export const useLeaveTransitionMutation = () => {
  const invalidate = useInvalidateLeaves();

  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'cancel' | 'close' }) =>
      leaveTransition(id, action),
    onSuccess: invalidate,
  });
};
